/**
 * CR306: Rendering section — JS rendering, wait/timeout settings
 */

import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Monitor, Save } from "lucide-react";
import type { Recipe } from "@/lib/recipes-studio/types";
import type { FirecrawlRecipeConfig } from "@/lib/recipes-studio/config-types";

interface RecipeRenderingSectionProps {
  recipe: Recipe;
  saving: boolean;
  onSave: (updates: Partial<Recipe>) => void;
}

export function RecipeRenderingSection({ recipe, saving, onSave }: RecipeRenderingSectionProps) {
  const cfg = (recipe.recipe_config ?? {}) as FirecrawlRecipeConfig;

  const [renderJs, setRenderJs] = useState(cfg.render_js ?? true);
  const [waitMs, setWaitMs] = useState(String(cfg.wait_ms ?? ""));
  const [timeoutMs, setTimeoutMs] = useState(String(cfg.timeout_ms ?? ""));

  useEffect(() => {
    const c = (recipe.recipe_config ?? {}) as FirecrawlRecipeConfig;
    setRenderJs(c.render_js ?? true);
    setWaitMs(String(c.wait_ms ?? ""));
    setTimeoutMs(String(c.timeout_ms ?? ""));
  }, [recipe.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const hasChanges =
    renderJs !== (cfg.render_js ?? true) ||
    waitMs !== String(cfg.wait_ms ?? "") ||
    timeoutMs !== String(cfg.timeout_ms ?? "");

  const handleSave = useCallback(() => {
    const updated: FirecrawlRecipeConfig = {
      ...cfg,
      render_js: renderJs,
      wait_ms: waitMs ? Number(waitMs) : undefined,
      timeout_ms: timeoutMs ? Number(timeoutMs) : undefined,
    };
    onSave({ recipe_config: updated as Record<string, unknown> });
  }, [cfg, renderJs, waitMs, timeoutMs, onSave]);

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <Monitor className="h-4 w-4 text-muted-foreground" />
          Rendering
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <Label className="text-xs">JavaScript Rendering</Label>
            <p className="text-[11px] text-muted-foreground">Enable for JS-heavy pages</p>
          </div>
          <Switch checked={renderJs} onCheckedChange={setRenderJs} />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className="text-xs">Wait (ms)</Label>
            <Input
              type="number"
              min={0}
              max={30000}
              value={waitMs}
              onChange={(e) => setWaitMs(e.target.value)}
              placeholder="2000"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Timeout (ms)</Label>
            <Input
              type="number"
              min={0}
              max={120000}
              value={timeoutMs}
              onChange={(e) => setTimeoutMs(e.target.value)}
              placeholder="30000"
            />
          </div>
        </div>

        {hasChanges && (
          <Button size="sm" onClick={handleSave} disabled={saving}>
            <Save className="h-3.5 w-3.5 mr-1.5" />
            {saving ? "Saving…" : "Save"}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
